import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ReactTags, Tag } from 'react-tag-autocomplete';
import { notify } from '@NOTIFICATION/Notificator';
import classnames from 'classnames/bind';
import { Typography } from '@mui/material';
import CircularProgressIndicator from '@COMPONENTS/SHARED/CircularProgressIndicator';
import Lock from '@COMPONENTS/SHARED/Lock';
import COLOR from '@CONSTANTS/COLOR.constant';
import { Tag as TagIcon } from 'lucide-react';
import styles from './ModernTagsComponent.module.scss';

const cx: CX = classnames.bind(styles);

interface TagData {
    id: number;
    name: string;
}

interface ModernTagsComponentProps {
    tags: TagData[];
    locked?: boolean;
    tagQuery: [
        any,
        {
            isLoading: boolean;
            isError: boolean;
            isSuccess: boolean;
            error?: any;
            originalArgs?: any;
        },
    ];
    apiParams: {
        chapterId?: number;
        sectionId?: number;
        id: number;
        documentId?: number; // Added for document-level operations
    };
    getAllTagsQuery: any;
    getTagsParams: {
        documentId: number;
    };
    addTagQuery: [
        any,
        {
            isLoading: boolean;
            isError: boolean;
            isSuccess: boolean;
            error?: any;
            originalArgs?: any;
        },
    ];
    addTagParams: {
        chapterId?: number;
        sectionId?: number;
        id: number;
    };
    createAndAddTagQuery: [
        any,
        {
            isLoading: boolean;
            isError: boolean;
            isSuccess: boolean;
            error?: any;
            originalArgs?: any;
        },
    ];
    createAndAddTagParams: {
        documentId: number;
        chapterId?: number;
        sectionId?: number;
        id: number;
    };
}

function ModernTagsComponent(props: ModernTagsComponentProps) {
    const {
        tags,
        locked = false,
        tagQuery,
        apiParams,
        getAllTagsQuery,
        getTagsParams,
        addTagQuery,
        addTagParams,
        createAndAddTagQuery,
        createAndAddTagParams,
    } = props;

    const [showInput, setShowInput] = useState(false);

    const [
        deleteTag,
        {
            isLoading: isDeleteTagLoading,
            isSuccess: isDeleteTagSuccess,
            isError: isDeleteTagError,
            error: deleteTagError,
        },
    ] = tagQuery;

    const [addTag, {
        isLoading: isAddLoading, isSuccess: isAddSuccess, isError: isAddError, error: addError,
    }] = addTagQuery;

    const [
        createAndAddTag,
        {
            isLoading: isCreateLoading,
            isSuccess: isCreateSuccess,
            isError: isCreateError,
            error: createError,
        },
    ] = createAndAddTagQuery;

    // Get all available tags for suggestions
    const {
        data: allTagsData = { data: [] },
    } = getAllTagsQuery(getTagsParams);

    // Transform current tags to react-tag-autocomplete format
    const selectedTags: Tag[] = useMemo(
        () => tags.map((tag) => ({
            value: tag.id.toString(),
            label: tag.name,
        })),
        [tags],
    );

    // Reset to empty state when all tags are removed
    useEffect(() => {
        if (selectedTags.length === 0 && showInput) {
            const timer = setTimeout(() => {
                setShowInput(false);
            }, 2000); // Wait 2 seconds before returning to empty state
            
            return () => clearTimeout(timer);
        }
    }, [selectedTags.length, showInput]);

    // Create suggestions from all available tags (excluding already selected ones)
    const suggestions: Tag[] = useMemo(
        () => allTagsData.data
            .filter((tag: TagData) => !tags.some((selectedTag) => selectedTag.id === tag.id))
            .map((tag: TagData) => ({
                value: tag.id.toString(),
                label: tag.name,
            })),
        [allTagsData.data, tags],
    );

    // Handle notifications
    useEffect(() => {
        if (isDeleteTagSuccess) {
            notify.info('Tag has been deleted', {
                toastId: 'delete-tag-success',
            });
        }
    }, [isDeleteTagSuccess]);

    useEffect(() => {
        if (isAddSuccess || isCreateSuccess) {
            notify.info('Tag has been added', {
                toastId: 'add-tag-success',
            });
        }
    }, [isAddSuccess, isCreateSuccess]);

    useEffect(() => {
        if (isDeleteTagError) {
            if ((deleteTagError as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'delete-tag-error-permission',
                });
            } else {
                notify.error('Something went wrong while deleting tag!', {
                    toastId: 'delete-tag-error',
                });
            }
        }
    }, [isDeleteTagError, deleteTagError]);

    useEffect(() => {
        if (isAddError || isCreateError) {
            const error = addError || createError;
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'add-tag-error-permission',
                });
            } else {
                notify.error('Something went wrong while adding tag!', {
                    toastId: 'add-tag-error',
                });
            }
        }
    }, [isAddError, isCreateError, addError, createError]);

    // Handle adding existing tag
    const handleAddTag = useCallback((newTag: Tag) => {
        const existingTag = allTagsData.data.find((tag: TagData) => tag.name === newTag.label);
        
        if (existingTag) {
            // Add existing tag
            addTag({
                ...addTagParams,
                tagId: existingTag.id,
            });
        } else {
            // Create and add new tag
            // Handle both document-level (uses 'name') and entity-level (uses 'tagName') APIs
            const createParams = createAndAddTagParams.chapterId === 0 
                ? { ...createAndAddTagParams, name: newTag.label } // Document level
                : { ...createAndAddTagParams, tagName: newTag.label }; // Chapter/Section/Variant level
                
            createAndAddTag(createParams);
        }
    }, [addTag, createAndAddTag, addTagParams, createAndAddTagParams, allTagsData.data]);

    // Handle removing tag
    const handleDeleteTag = useCallback((index: number) => {
        const tagToDelete = selectedTags[index];
        if (tagToDelete?.value) {
            const tagId = parseInt(String(tagToDelete.value), 10);
            
            // Handle different API parameter formats
            if (!apiParams.chapterId || apiParams.chapterId === 0) {
                // Document-level tag deletion: useDeleteTag expects {id: tagId, documentId}
                deleteTag({
                    id: tagId,
                    documentId: apiParams.documentId || getTagsParams.documentId,
                });
            } else {
                // Entity-level tag deletion: expects {id: entityId, tagId, ...otherParams}
                const deleteParams: any = {
                    id: apiParams.id,
                    tagId,
                };
                
                // Add required parameters based on entity level
                if (apiParams.chapterId) {
                    deleteParams.chapterId = apiParams.chapterId;
                }
                if (apiParams.sectionId) {
                    deleteParams.sectionId = apiParams.sectionId;
                }
                if (apiParams.documentId) {
                    deleteParams.documentId = apiParams.documentId;
                } else {
                    deleteParams.documentId = getTagsParams.documentId;
                }
                
                deleteTag(deleteParams);
            }
        }
    }, [deleteTag, apiParams, selectedTags, getTagsParams.documentId]);

    const isProcessing = isDeleteTagLoading || isAddLoading || isCreateLoading;

    if (locked) {
        return (
            <div className={cx('modern-tags-container', 'locked')}>
                <div className={cx('inline-wrapper')}>
                    <div className={cx('title-wrapper')}>
                        <Typography variant="body2" className={cx('title')}>
                            Tags
                        </Typography>
                        <div className={cx('lock-wrapper')}>
                            <Lock size="small" />
                        </div>
                    </div>
                    <div className={cx('tags-wrapper', 'locked')}>
                        {selectedTags.length > 0 ? (
                            selectedTags.map((tag) => (
                                <div key={`locked-tag-${String(tag.value)}`} className={cx('tag-item', 'locked')}>
                                    <span className={cx('tag-label')}>{tag.label}</span>
                                </div>
                            ))
                        ) : (
                            <div className={cx('no-tags')}>No tags</div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cx('modern-tags-container')}>
            <div className={cx('inline-wrapper')}>
                <div className={cx('title-wrapper')}>
                    <Typography variant="body2" className={cx('title')}>
                        Tags
                    </Typography>
                    {isProcessing && (
                        <div className={cx('progress-indicator')}>
                            <CircularProgressIndicator
                                color={COLOR['marriott-primary']}
                                size={16}
                                thickness={2}
                            />
                        </div>
                    )}
                </div>
                <div className={cx('react-tags-wrapper')}>
                    {selectedTags.length === 0 && !showInput ? (
                        <div 
                            className={cx('empty-state')} 
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                setShowInput(true);
                                // Focus the input after ReactTags renders
                                setTimeout(() => {
                                    const input = document.querySelector('.react-tags__input') as HTMLInputElement;
                                    if (input) input.focus();
                                }, 50);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    setShowInput(true);
                                    setTimeout(() => {
                                        const input = document.querySelector('.react-tags__input') as HTMLInputElement;
                                        if (input) input.focus();
                                    }, 50);
                                }
                            }}
                        >
                            <div className={cx('empty-content')}>
                                <div className={cx('icon-wrapper')}>
                                    <TagIcon className={cx('tag-icon')} />
                                </div>
                                <div className={cx('text-content')}>
                                    <span className={cx('main-text')}>Add your first tag</span>
                                    <span className={cx('sub-text')}>Click here to start typing</span>
                                </div>
                            </div>
                            <div className={cx('shimmer')} />
                        </div>
                    ) : (
                        <ReactTags
                            selected={selectedTags}
                            suggestions={suggestions}
                            onAdd={handleAddTag}
                            onDelete={handleDeleteTag}
                            placeholderText=""
                            allowNew
                            activateFirstOption
                            classNames={{
                                root: cx('react-tags'),
                                rootIsActive: cx('react-tags--active'),
                                rootIsDisabled: cx('react-tags--disabled'),
                                rootIsInvalid: cx('react-tags--invalid'),
                                label: cx('react-tags__label'),
                                tagList: cx('react-tags__list'),
                                tagListItem: cx('react-tags__list-item'),
                                tag: cx('react-tags__tag'),
                                tagName: cx('react-tags__tag-name'),
                                comboBox: cx('react-tags__combobox'),
                                input: cx('react-tags__input'),
                                listBox: cx('react-tags__listbox'),
                                option: cx('react-tags__option'),
                                optionIsActive: cx('react-tags__option--active'),
                                highlight: cx('react-tags__option-highlight'),
                            }}
                            isDisabled={isProcessing}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

ModernTagsComponent.defaultProps = {
    locked: false,
};

export default ModernTagsComponent; 